import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import { FreePracticeCard } from '@/widgets/free-practice';
import { TopicPickerEntryCard } from '@/widgets/topic-picker-entry';
import { cn } from '@/shared/lib/cn';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';

const SLIDES = [FreePracticeCard, TopicPickerEntryCard];

/**
 * Карусель двух промо-карточек главного экрана («Свободная практика» и
 * «Рулетка тем»): раньше шли одна под другой, теперь свайпаются как один
 * слайдер. `loop: true` — с двух карточек можно свайпать вправо бесконечно
 * (embla сама клонирует слайды на стыке), не только вперёд-назад.
 *
 * Заголовок — снаружи карусели, тем же приёмом, что «Готовые темы»
 * (`widgets/topics`): `h2` над контентом, а не внутри карточки. Текст —
 * «Свободная практика», тот же, что и заголовок внутри `FreePracticeCard`
 * (у неё свой остался, см. её файл) — на втором слайде (рулетка тем) внешний
 * заголовок этому не соответствует, но так решил продукт.
 */
export function HomePromoCarousel() {
  const { t } = useTranslation('HomePromoCarousel');
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const desktop = useInDesktopShell();

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleSelect);
    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  // На компьютере колонке хватает места на обе карточки — рядом, плитками
  // одной высоты, без свайпа и точек: свайп мышью неочевиден.
  if (desktop) {
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>
        <div className="grid grid-cols-2 gap-4 [&>*]:min-w-0 [&>*>*]:h-full">
          {SLIDES.map((Slide, index) => (
            <div key={index}>
              <Slide />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>

      {/* Зазор между слайдами — `pl-3` на каждом плюс компенсирующий
          `-ml-3` на треке, а не `gap` на flex-контейнере: embla меряет
          ширину слайда вместе с его паддингом, `gap` она не учитывает и
          съезжает на стыке зацикливания.
          `py-2 -my-2` на вьюпорте — карточкам нужен `overflow-hidden`
          снаружи (иначе свайп не будет обрезать соседний слайд), но он же
          клипает их собственную тень по нижнему краю вплотную к границе
          контейнера. Паддинг даёт тени место отрисоваться, отрицательный
          margin той же величины утягивает вьюпорт обратно — внешний `gap-2`
          между каруселью и точками не меняется. */}
      <div className="-my-2 overflow-hidden py-2" ref={emblaRef}>
        <div className="-ml-3 flex touch-pan-y">
          {SLIDES.map((Slide, index) => (
            <div key={index} className="min-w-0 shrink-0 grow-0 basis-full pl-3">
              <Slide />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
        {SLIDES.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => scrollTo(index)}
            aria-label={`${index + 1}`}
            className={cn('h-1.5 rounded-full transition-all', index === selectedIndex ? 'w-5 bg-ink' : 'w-1.5 bg-ink/20')}
          />
        ))}
      </div>
    </div>
  );
}
